import { useState, type ReactNode } from "react";
import { WeeklyBars } from "../components/LazyCharts";
import { FunnelBars, APP_STORE_ONLY_NOTE, RetentionCard, UsageKpiCard, useUsage } from "../components/usage";
import { funnelIsEmpty } from "../lib/usage";
import { ActiveOnPhoneCard, DeviceSplits, useDevices } from "../components/devices";
import { EnvVatPeriodControls } from "../components/Controls";
import { PageHeader, useShell } from "../components/Shell";
import { AppIcon, BarList, Card, Chip, CommandCenterButton, EmptyState, ErrorState, KpiCard, ProviderSplitCard, TableWrap } from "../components/ui";
import { RatingCard, RpiKpi } from "../components/insights";
import { PanelSkeleton } from "../components/skeletons";
import { ApiError, getAppDetail, getAppMetrics, transactionsCsvUrl, type AdminAppDetail } from "../lib/api-admin";
import { PERIOD_LONG, VAT_LABELS, countryNl, dateTimeNl, eur, int, kindMeta, linkStatusMeta, pct, periodNl } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { Link, useLocation } from "../lib/router";
import { useAsync } from "../lib/useAsync";
import { useIsMobile } from "../lib/useMobile";
import { EditableAppName } from "../components/EditableAppName";
import { AppDetailMobile } from "./AppDetailMobile";
import type { AppMetricsResponse } from "@apphq/shared/types";

type Tab = "omzet" | "gebruik" | "producten" | "transacties";
const TABS: Array<{ id: Tab; label: string }> = [
  { id: "omzet", label: "Omzet" },
  { id: "gebruik", label: "Gebruik" },
  { id: "producten", label: "Producten" },
  { id: "transacties", label: "Transacties" },
];

const SOURCE_LABEL: Record<string, string> = { asc: "Via App Store Connect", skill: "Via Claude-skill", webhook: "Via webhook" };

export function AppDetail({ id }: { id: string }) {
  const { env, period, vat } = usePrefs();
  const { search, setSearch } = useLocation();
  const tabParam = search.get("tab");
  const tab: Tab = TABS.some((t) => t.id === tabParam) ? (tabParam as Tab) : "omzet";

  const { refresh } = useShell();
  const mobile = useIsMobile();
  const detail = useAsync(() => getAppDetail(id), [id]);
  const metrics = useAsync(() => getAppMetrics(id, { period, environment: env, vat }), [id, period, env, vat]);

  if (detail.status === "error") {
    const notFound = /not found/i.test(detail.error);
    return (
      <>
        <PageHeader title="App" back={{ to: "/", label: "Overzicht" }} />
        {notFound ? <EmptyState title="App niet gevonden">Deze app bestaat niet (meer).</EmptyState> : <ErrorState message={detail.error} onRetry={detail.reload} />}
      </>
    );
  }
  const app = detail.data?.app;
  if (!app) {
    return (
      <>
        <PageHeader title="App" back={{ to: "/", label: "Overzicht" }} />
        <PanelSkeleton kpis={5} chart rows={6} />
      </>
    );
  }
  const onRenamed = () => {
    detail.reload();
    refresh();
  };
  // link status comes from the metrics API (same derivation as the overview); no chip until it has loaded
  const link = metrics.data ? linkStatusMeta(metrics.data.link_status) : null;

  if (mobile) {
    const mTab = tab === "gebruik" ? "gebruik" : "omzet";
    return (
      <AppDetailMobile
        app={app}
        onRenamed={onRenamed}
        metrics={metrics.data}
        tab={mTab}
        onTab={(t) => setSearch({ tab: t === "omzet" ? null : t })}
        usage={<UsageTab id={id} name={app.display_name ?? app.name} />}
        loading={metrics.status === "error" ? <ErrorState message={metrics.error} onRetry={metrics.reload} /> : <PanelSkeleton kpis={4} chart rows={4} />}
      />
    );
  }

  return (
    <>
      <PageHeader title={<EditableAppName app={app} onSaved={onRenamed} />} subtitle={app.bundle_id ?? "Geen bundle-ID"} back={{ to: "/", label: "Overzicht" }}>
        <CommandCenterButton url={app.command_center_url} appId={app.id} showUnset />
      </PageHeader>

      <div className="app-hero">
        <AppIcon name={app.display_name ?? app.name} url={app.icon_url} size={64} seed={app.id} />
        <div className="chips">
          {link && <Chip tone={link.tone}>{link.label}</Chip>}
          <Chip>{SOURCE_LABEL[app.source] ?? app.source}</Chip>
          {app.status !== "active" && <Chip tone="warn">{app.status === "pending" ? "In afwachting" : "Gearchiveerd"}</Chip>}
          {app.asc_synced_at && <Chip>Sync {dateTimeNl(app.asc_synced_at)}</Chip>}
        </div>
      </div>

      <div className="toolbar">
        <div className="segmented" role="tablist" aria-label="Onderdeel">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? "seg active" : "seg"} onClick={() => setSearch({ tab: t.id === "omzet" ? null : t.id })}>
              {t.label}
            </button>
          ))}
        </div>
        {(tab === "omzet" || tab === "transacties") && <EnvVatPeriodControls />}
      </div>

      {tab === "omzet" && <RevenueTab state={metrics} />}
      {tab === "gebruik" && <UsageTab id={id} name={app.display_name ?? app.name} />}
      {tab === "producten" && <ProductsTab products={detail.data?.products ?? []} metrics={metrics.data} />}
      {tab === "transacties" && <TransactionsTab id={id} state={metrics} />}
    </>
  );
}

/** Devices (phase 5) + DAU/MAU and retention (phase 6). */
function UsageTab({ id }: { id: string; name: string }) {
  const devices = useDevices(id);
  const usage = useUsage(id);
  return (
    <>
      <ActiveOnPhoneCard state={devices} />
      <DeviceSplits state={devices} />
      <UsageKpiCard state={usage} />
      <RetentionCard state={usage} />
    </>
  );
}

type MetricsState = ReturnType<typeof useAsync<AppMetricsResponse>>;

function Wait({ state, children }: { state: MetricsState; children: (d: AppMetricsResponse) => ReactNode }) {
  if (state.status === "error") return <ErrorState message={state.error} onRetry={state.reload} />;
  if (state.status === "loading") return <PanelSkeleton kpis={5} chart rows={6} />;
  return <>{children(state.data)}</>;
}

function RevenueTab({ state }: { state: MetricsState }) {
  const { vat, period } = usePrefs();
  return (
    <Wait state={state}>
      {(m) => {
        const k = m.kpis;
        return (
          <>
            <div className="kpi-grid">
              <KpiCard label="MRR" value={eur(k.mrr)} />
              <KpiCard label="Omzet (periode)" value={eur(k.revenue)} hint={`${VAT_LABELS[vat]} · ${PERIOD_LONG[period]}`} />
              <KpiCard label="Op telefoon" value={int(k.active_on_phone.value)} previous={{ current: k.active_on_phone.value, before: k.active_on_phone.previous }} hint="toestellen, 7 dagen" />
              <RpiKpi rpi={k.rpi} />
              <KpiCard label="Abonnees" value={int(k.active_subscriptions)} />
              <KpiCard label="Trials" value={int(k.active_trials)} />
              <KpiCard label="Trial → betaald" value={pct(k.trial_conversion)} hint={k.new_trials ? `${int(k.new_trials)} gestart` : undefined} />
              <KpiCard label="Churn (periode)" value={pct(k.churn_rate)} />
              <KpiCard label="LTV gerealiseerd" value={eur(k.ltv_realized)} />
            </div>

            {m.fx.missing > 0 && <div className="banner banner-warn">{m.fx.missing} transacties in {m.fx.currencies.join(", ")} zonder wisselkoers zijn niet meegeteld.</div>}

            <ProviderSplitCard split={m.revenue_by_provider} />

            <Card title="Nieuw versus opgezegd per week">
              {m.new_vs_cancelled.length === 0 ? <EmptyState title="Nog geen abonnementsbewegingen" /> : <WeeklyBars rows={m.new_vs_cancelled} />}
            </Card>

            <div className="grid-2">
              <Card title="Paywall-funnel">
                {funnelIsEmpty(m.funnel) ? (
                  <EmptyState title="Nog geen paywall-events" icon="⌄">
                    Bekeken → gestart → betaald komt uit de SDK-events.
                  </EmptyState>
                ) : (
                  <>
                    <FunnelBars funnel={m.funnel!} />
                    <p className="muted small tight">{APP_STORE_ONLY_NOTE}</p>
                  </>
                )}
              </Card>
              <Card title="Productmix">
                {m.product_mix.length === 0 || m.product_mix.every((p) => p.revenue === 0 && p.active === 0) ? (
                  <EmptyState title="Nog geen omzet per product" />
                ) : (
                  <BarList rows={m.product_mix.map((p) => ({ key: p.product_id, label: <span className="mono">{p.product_id}</span>, value: p.revenue }))} />
                )}
              </Card>
            </div>

            <Card title="Top-landen">
              {m.top_countries.length === 0 ? (
                <EmptyState title="Nog geen omzet per land" />
              ) : (
                <BarList rows={m.top_countries.map((c) => ({ key: c.country, label: `${countryNl(c.country)} · ${int(c.transactions)}×`, value: c.revenue }))} />
              )}
            </Card>

            <RatingCard ratings={m.ratings} />
          </>
        );
      }}
    </Wait>
  );
}

function ProductsTab({ products, metrics }: { products: AdminAppDetail["products"]; metrics: AppMetricsResponse | null }) {
  if (products.length === 0) return <Card title="Producten"><EmptyState title="Nog geen producten">Producten worden automatisch ontdekt bij de eerste aankoop of notificatie.</EmptyState></Card>;
  const mix = new Map((metrics?.product_mix ?? []).map((p) => [p.product_id, p]));
  return (
    <Card title="Producten">
      <TableWrap>
        <table>
          <thead>
            <tr>
              <th>Product-ID</th>
              <th>Type</th>
              <th>Periode</th>
              <th className="num">Actief</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.id}</td>
                <td>{p.type === "subscription" ? "Abonnement" : p.type === "non_consumable" ? "Eenmalig (blijvend)" : p.type === "consumable" ? "Verbruiksartikel" : p.type}</td>
                <td>{periodNl(p.period)}</td>
                <td className="num">{mix.has(p.id) ? int(mix.get(p.id)?.active ?? 0) : "—"}</td>
                <td>{p.status === "active" ? <Chip tone="good">Actief</Chip> : <Chip tone="warn">Automatisch ontdekt · controleren</Chip>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Card>
  );
}

function TransactionsTab({ id, state }: { id: string; state: MetricsState }) {
  const { vat, env, period } = usePrefs();
  const [includeUser, setIncludeUser] = useState(false);
  return (
    <Wait state={state}>
      {(m) => (
        <Card
          title="Transacties"
          aside={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={includeUser} onChange={(e) => setIncludeUser(e.target.checked)} />
                incl. klant-ID
              </label>
              <a
                className="pill"
                href={transactionsCsvUrl(id, { period, environment: env, vat }, { includeUser })}
                download
                title={`Alle transacties van deze periode (${PERIOD_LONG[period]}, ${VAT_LABELS[vat]}), max. 366 dagen`}
              >
                CSV-export
              </a>
            </span>
          }
        >
          {m.recent_transactions.length === 0 ? (
            <EmptyState title="Geen transacties in deze periode" />
          ) : (
            <TableWrap>
              <table>
                <thead>
                  <tr>
                    <th>Tijd</th>
                    <th>Klant</th>
                    <th>Soort</th>
                    <th>Product</th>
                    <th>Land</th>
                    <th className="num">{VAT_LABELS[vat]}</th>
                    <th className="num">Opbrengst (schatting)</th>
                  </tr>
                </thead>
                <tbody>
                  {m.recent_transactions.map((t) => {
                    const kind = kindMeta(t.kind);
                    return (
                      <tr key={t.id}>
                        <td>{dateTimeNl(t.occurred_at)}</td>
                        <td className="mono">
                          {t.app_user_id ? <Link to={`/klanten/${encodeURIComponent(t.app_user_id)}?app_id=${encodeURIComponent(m.app_id)}`} className="link">{t.app_user_id.slice(0, 8)}…</Link> : "—"}
                        </td>
                        <td>
                          <Chip tone={kind.tone}>{kind.label}</Chip>
                          {t.provider === "stripe" && <> <Chip>Stripe</Chip></>}
                        </td>
                        <td className="mono">{t.product_id}</td>
                        <td>{countryNl(t.country)}</td>
                        <td className="num" title={t.amount_eur === null ? "Geen wisselkoers beschikbaar" : undefined}>{eur(t.amount_eur)}</td>
                        <td className="num">{eur(t.proceeds_eur)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>
          )}
        </Card>
      )}
    </Wait>
  );
}
